import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Minus, Plus, ShoppingCart, ChevronRight, ShieldCheck, FileCheck2,
  Truck, FlaskConical, BadgeCheck, ArrowLeft,
} from "lucide-react";
import { PRODUCTS, STOCK_LABELS, CATEGORIES } from "../mock";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";
import ProductCard from "../components/ProductCard";
import VialRender from "../components/VialRender";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const toneClasses = {
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  red: "bg-red-50 text-red-600 ring-red-200",
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { toast } = useToast();
  const [qty, setQty] = useState(1);

  const product = PRODUCTS.find((p) => p.id === id);

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display font-bold text-2xl text-slate-900">Product not found</h1>
        <Link to="/shop" className="mt-4 inline-block text-blue-600 font-semibold hover:underline">← Back to shop</Link>
      </div>
    );
  }

  const stock = STOCK_LABELS[product.stock] || STOCK_LABELS["in-stock"];
  const soldOut = product.stock === "out-of-stock";
  const isPreorder = product.stock === "pre-order";
  const catName = CATEGORIES.find((c) => c.slug === product.category)?.name || "Research";
  const related = PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAdd = () => {
    if (soldOut) return;
    addItem(product, qty);
    toast({ title: "Added to cart", description: `${qty} × ${product.name}` });
  };

  const buyNow = () => {
    if (soldOut) return;
    addItem(product, qty);
    navigate("/cart");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      {/* breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-blue-600">Home</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to={`/shop?cat=${product.category}`} className="hover:text-blue-600">{catName}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-slate-800 font-medium truncate">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* image */}
        <div>
          <div className="relative rounded-3xl overflow-hidden border border-slate-200 aspect-square">
            <VialRender product={product} />
            <span className={`absolute top-4 left-4 z-10 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ${toneClasses[stock.tone]}`}>
              {product.stock === "low" ? `${product.stockCount} Left` : stock.label}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { icon: FlaskConical, t: "HPLC", s: product.purity },
              { icon: BadgeCheck, t: "CoA", s: "On file" },
              { icon: ShieldCheck, t: "Batch", s: "Verified" },
            ].map((f) => (
              <div key={f.t} className="rounded-xl border border-slate-200 p-3 text-center">
                <f.icon className="h-5 w-5 text-blue-600 mx-auto" />
                <div className="mt-1 text-xs font-bold text-slate-900">{f.t}</div>
                <div className="text-[11px] text-slate-500">{f.s}</div>
              </div>
            ))}
          </div>
        </div>

        {/* info */}
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-blue-600">{catName}</span>
          <h1 className="mt-1 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">{product.name}</h1>
          <p className="mt-2 text-xs text-slate-400 font-mono">SKU: {product.sku}</p>

          <div className="mt-5 flex items-end gap-3">
            <span className="font-display font-extrabold text-4xl text-slate-900">${product.price.toFixed(2)}</span>
            {product.compareAt && (
              <>
                <span className="text-lg text-slate-400 line-through mb-1">${product.compareAt.toFixed(2)}</span>
                <span className="mb-2 rounded-full bg-blue-100 text-blue-700 px-2.5 py-1 text-xs font-bold">
                  Save ${(product.compareAt - product.price).toFixed(2)}
                </span>
              </>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">AUD · per vial · {product.size}</p>

          <p className="mt-5 text-slate-600 leading-relaxed">{product.description}</p>

          {/* qty + actions */}
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <div className="inline-flex items-center rounded-xl border border-slate-200 bg-white">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-11 flex items-center justify-center text-slate-600 hover:text-blue-600" aria-label="Decrease">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-bold text-slate-900">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(99, q + 1))} className="h-11 w-11 flex items-center justify-center text-slate-600 hover:text-blue-600" aria-label="Increase">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={handleAdd}
              disabled={soldOut}
              className={`inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold transition-all ${
                soldOut ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]"
              }`}
            >
              <ShoppingCart className="h-4.5 w-4.5" />
              {soldOut ? "Sold Out" : isPreorder ? "Pre-Order" : "Add to Cart"}
            </button>
            {!soldOut && (
              <button onClick={buyNow} className="inline-flex items-center gap-2 rounded-xl border border-blue-600 text-blue-700 px-6 py-3 font-semibold hover:bg-blue-50 transition-colors">
                Buy Now
              </button>
            )}
          </div>

          {/* assurances */}
          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {[
              { icon: Truck, t: "Same-day dispatch", s: "Mon–Fri before 12pm AEST" },
              { icon: FileCheck2, t: "Published CoA", s: "Linked to your batch ID" },
              { icon: ShieldCheck, t: "Lost/damaged cover", s: "Resend or store credit" },
              { icon: BadgeCheck, t: "99%+ HPLC purity", s: "Third-party verified" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-100 p-3">
                <f.icon className="h-5 w-5 text-blue-600 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{f.t}</div>
                  <div className="text-xs text-slate-500">{f.s}</div>
                </div>
              </div>
            ))}
          </div>

          {/* accordions */}
          <Accordion type="single" collapsible className="mt-7">
            <AccordionItem value="details">
              <AccordionTrigger className="text-sm font-semibold">Specifications</AccordionTrigger>
              <AccordionContent>
                <dl className="grid grid-cols-2 gap-y-2 text-sm">
                  <dt className="text-slate-500">Vial size</dt><dd className="text-slate-800 font-medium">{product.size}</dd>
                  <dt className="text-slate-500">Purity</dt><dd className="text-slate-800 font-medium">{product.purity} HPLC</dd>
                  <dt className="text-slate-500">Form</dt><dd className="text-slate-800 font-medium">Lyophilised powder</dd>
                  <dt className="text-slate-500">CoA</dt><dd className="text-slate-800 font-medium">On file</dd>
                  <dt className="text-slate-500">SKU</dt><dd className="text-slate-800 font-medium">{product.sku}</dd>
                </dl>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="storage">
              <AccordionTrigger className="text-sm font-semibold">Handling &amp; Storage</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Store lyophilised vials at -20°C away from light. After reconstitution with bacteriostatic water,
                  refrigerate at 2–8°C. For laboratory handling by qualified researchers only.
                </p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping">
              <AccordionTrigger className="text-sm font-semibold">Shipping &amp; Returns</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Dispatched via AusPost Express with full tracking. Free shipping on orders over $220 AUD. Anything
                  lost or damaged in transit is resent or credited.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Research Use Only:</span> Not for human or veterinary consumption.
            Sold exclusively for in-vitro laboratory research to qualified researchers aged 18+.
          </div>
        </div>
      </div>

      {/* related */}
      {related.length > 0 && (
        <div className="mt-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-extrabold text-2xl text-slate-900">Related Compounds</h2>
            <Link to={`/shop?cat=${product.category}`} className="text-blue-600 font-semibold text-sm hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}

      <div className="mt-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 font-semibold text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to all products
        </Link>
      </div>
    </div>
  );
};

export default ProductDetail;
